import { useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
} from "@salestrak/ui";

import { useAuth } from "#app/lib/auth-context";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";

interface LogoutConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opened the confirm, for `auth.sign_out`. */
  via: string;
}

/**
 * Confirmation for every user-facing "Log out" affordance. Owns the sign-out
 * call and its analytics event so each menu only manages open state — a
 * programmatic sign-out (e.g. a cancelled invite) bypasses this on purpose.
 */
export function LogoutConfirmDialog({
  open,
  onOpenChange,
  via,
}: LogoutConfirmDialogProps) {
  const { signOut } = useAuth();
  const { track } = useAnalytics();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleConfirm() {
    setLoggingOut(true);
    try {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignOut, data: { via } });
      await signOut();
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>Log out</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to log out?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={loggingOut}
          >
            Cancel
          </AlertDialogClose>
          <Button onClick={handleConfirm} loading={loggingOut}>
            Log out
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
